'use client'

import { useRef } from 'react'

import { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { RevealH2, RevealSection } from '@/components/RevealSection'
import RichText from '@/components/RichText'
import { AnimatedAccordion, AnimatedAccordionItem } from '@/components/ui/animated-accordion'

interface NavFaqType {
  sections: {
    sectionHeading: string
    items: {
      id?: string | null
      question: string
      answer: DefaultTypedEditorState
    }[]
  }[]
}

export const NavFaq: React.FC<NavFaqType> = ({ sections }) => {
  const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  return (
    <>
      <div className="wrapper flex gap-10 pb-20 lg:grid lg:grid-cols-[0.75fr_0.25fr] xl:gap-24 wrapper:px-10">
        <div className="relative flex w-full max-w-210 flex-col gap-14">
          {sections.map((section) => (
            <div
              key={section.sectionHeading}
              id={section.sectionHeading}
              ref={(el) => {
                questionRefs.current[section.sectionHeading] = el
              }}
              className="flex scroll-mt-[25vh] flex-col gap-4 pb-6"
            >
              <RevealH2 className="mb-6 font-family-body text-2xl font-semibold">
                {section.sectionHeading}
              </RevealH2>
              <RevealSection stagger className="flex flex-col gap-5 wrapper:gap-10">
                {section.items.map((item, index) => (
                  <AnimatedAccordion
                    key={item.id || `nav-faq-item-${index}`}
                    className="w-full max-w-full rounded-xl border-none! bg-neutral-100 [&_section]:border-none!"
                  >
                    <AnimatedAccordionItem
                      header={item.question}
                      buttonClassName=" [&>span]:text-base [&>span]:sm:text-lg [&>span]:font-medium"
                    >
                      <RichText data={item.answer as DefaultTypedEditorState} />
                    </AnimatedAccordionItem>
                  </AnimatedAccordion>
                ))}
              </RevealSection>
            </div>
          ))}
        </div>
        <div className="sticky top-16 mr-8 hidden flex-row self-start lg:flex xl:mr-12">
          <RevealSection
            stagger
            className="flex flex-col gap-4 border-l-[1.5px] border-neutral-200 pl-12"
          >
            {sections.map((section) => (
              <a
                key={section.sectionHeading}
                href={`#${section.sectionHeading}`}
                className="font-family-body text-base text-neutral-600 hover:font-semibold hover:text-neutral-900"
                onClick={(e) => {
                  e.preventDefault()
                  questionRefs.current[section.sectionHeading]?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }}
              >
                {section.sectionHeading}
              </a>
            ))}
          </RevealSection>
        </div>
      </div>
    </>
  )
}
